import * as React from 'react';
import { Route, Redirect, Switch } from 'react-router';
import { Navigator } from '../Navigator';
import { PlaceListPage } from './PlaceListPage';
import { PlaceCreatePage } from './PlaceCreatePage';
import { PlacePage } from './PlacePage';
import { MyPage } from './MyPage';
import { AccountListPage } from './AccountListPage';
import { CustomerListPage } from './CustomerListPage';
import { ShapeTileListPage } from './ShapeTileListPage';
import { FrameListPage } from './FrameListPage';
import { AdminCreateAccountPage } from './AdminCreateAccountPage';
import { AdminCreateCustomerPage } from './AdminCreateCustomerPage';
import { AccountCreatePasswordPage } from './AccountCreatePasswordPage';
import { AdminCreateFramePage } from './AdminCreateFramePage';
import { ConfigCreateShapeTilePage } from './AdminCreateShapeTilePage';
import { NotFoundPage } from './NotFoundPage';
import { DeviceStatusPage } from './DeviceStatusPage';
import { SuperAdminPage } from './SuperAdminPage';
import { RequireAuthority } from '../components/RequireAuthority';
import { Authority } from '../components/AuthorityLabel';

/**
 * 共通設定（/configs配下）の画面。マスター編集者以上の利用者だけが表示できる。
 */
const ConfigRoutes: React.FC = function () {
  return (
    <RequireAuthority level={Authority.MasterAdmin}>
      <Switch>
        <Route exact path="/configs/shape_tiles">
          <ShapeTileListPage />
        </Route>
        <Route exact path="/configs/shape_tiles/new">
          <ConfigCreateShapeTilePage />
        </Route>
        <Route
          exact
          path="/configs/shape_tiles/:shapeTileId/edit"
          render={(props) => (
            <ConfigCreateShapeTilePage
              key={props.match.params.shapeTileId}
              existingId={parseInt(props.match.params.shapeTileId)}
            />
          )}
        />
        <Route exact path="/configs/frames">
          <FrameListPage />
        </Route>
        <Route exact path="/configs/frames/new">
          <AdminCreateFramePage />
        </Route>
        <Route
          exact
          path="/configs/frames/:frameId/edit"
          render={(props) => (
            <AdminCreateFramePage
              key={props.match.params.frameId}
              existingId={parseInt(props.match.params.frameId)}
            />
          )}
        />
        <Route component={NotFoundPage} />
      </Switch>
    </RequireAuthority>
  );
};

/**
 * 管理（/admin配下）の画面。管理者以上の利用者だけが表示できる。
 */
const AdminRoutes: React.FC = function () {
  return (
    <RequireAuthority level={Authority.Admin}>
      <Switch>
        <Route exact path="/admin/accounts">
          <AccountListPage />
        </Route>
        <Route exact path="/admin/accounts/new">
          <AdminCreateAccountPage />
        </Route>
        <Route
          exact
          path="/admin/accounts/:accountId/edit"
          render={(props) => (
            <AdminCreateAccountPage
              key={props.match.params.accountId}
              existingId={parseInt(props.match.params.accountId)}
            />
          )}
        />
        <Route
          exact
          path="/admin/accounts/:accountId/password/edit"
          render={(props) => (
            <AccountCreatePasswordPage
              key={props.match.params.accountId}
              backPath="/admin/accounts"
              target={parseInt(props.match.params.accountId)}
            />
          )}
        />
        <Route exact path="/admin/clients">
          <CustomerListPage />
        </Route>
        <Route exact path="/admin/clients/new">
          <AdminCreateCustomerPage />
        </Route>
        <Route
          exact
          path="/admin/clients/:customerId/edit"
          render={(props) => (
            <AdminCreateCustomerPage
              key={props.match.params.customerId}
              existingId={parseInt(props.match.params.customerId)}
            />
          )}
        />
        <Route exact path="/admin/devices">
          <DeviceStatusPage />
        </Route>
        <Route component={NotFoundPage} />
      </Switch>
    </RequireAuthority>
  );
};

/**
 * トップページ
 */
export const IndexPage: React.FC = function () {
  return (
    <div className="App-content">
      <div className="App-navigator">
        <Navigator />
      </div>
      <main className="App-main">
        <Switch>
          <Redirect exact from="/" to="/places" />
          {/* 変更前のURLをブックマークから開いたときに、新しいURLへ転送する */}
          <Redirect exact from="/dashboard" to="/account" />
          <Redirect exact from="/configs" to="/configs/shape_tiles" />
          <Redirect exact from="/admin" to="/admin/accounts" />
          <Redirect exact from="/admin/frames/new" to="/configs/frames/new" />
          <Redirect
            exact
            from="/admin/frames/:frameId/edit"
            to="/configs/frames/:frameId/edit"
          />
          <Redirect
            exact
            from="/admin/shape_tiles/new"
            to="/configs/shape_tiles/new"
          />
          <Redirect
            exact
            from="/admin/shape_tiles/:shapeTileId/edit"
            to="/configs/shape_tiles/:shapeTileId/edit"
          />
          <Redirect exact from="/admin/device_status" to="/admin/devices" />
          {/* 以前のアーカイブ画面の URL。履歴やブックマークから開かれても物件ページ扱い（id=archive）にならないよう一覧へ戻す */}
          <Redirect exact from="/places/archive" to="/places" />
          <Route exact path="/places">
            <PlaceListPage />
          </Route>
          <Route exact path="/places/new">
            <PlaceCreatePage />
          </Route>
          <Route path="/places/:id" component={PlacePage} />
          <Route exact path="/account">
            <MyPage />
          </Route>
          <Route exact path="/password/edit">
            <AccountCreatePasswordPage backPath="/account" target="me" />
          </Route>
          <Route path="/configs">
            <ConfigRoutes />
          </Route>
          <Route path="/admin">
            <AdminRoutes />
          </Route>
          <Route exact path="/superadmin">
            <RequireAuthority level={Authority.SuperAdmin}>
              <SuperAdminPage />
            </RequireAuthority>
          </Route>
          <Route component={NotFoundPage} />
        </Switch>
      </main>
    </div>
  );
};
